import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';

import { Field, Fields, reduxForm } from 'redux-form';

import IconButton from 'components/common/IconButton';
import RadioButtonField from 'components/fields/RadioButtonField';
import RadioGroupField from 'components/fields/RadioGroupField';
import DatepickerField from 'components/fields/DatepickerField';
import AutosuggestField from 'components/fields/AutosuggestField';
import TextField from 'components/fields/TextField';
import IconRefresh from 'components/icons/IconRefresh';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

import dateFormat from 'config/dateFormat';
import orderStates from 'config/orderStates';
import activityLogEventTypes from 'config/activityLogEventTypes';

import { requestSearchUsers } from 'redux/modules/users';
import { accountLoginFail } from 'redux/modules/account';

const styles = theme => ({
  root: {
    padding: 24,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
  iconRefresh: {
    display: 'block',
    width: 16,
    height: 16,
    // margin: '0 16px 0 20px',
    color: theme.palette.secondary[650],
  },
  buttonClear: {
    margin: -9,
  },
});

function onChange(values, dispatch, props, previousValues) {
  if (values['Notification Type'] !== values['Selected Notification Type'] && values['Notification Type ID']) {
    props.clearFields(false, false, 'Selected Notification Type');
    props.clearFields(false, false, 'Notification Type ID');
  }

  if (values['User Name'] !== values['Selected User Name'] && values['User ID']) {
    props.clearFields(false, false, 'Selected User Name');
    props.clearFields(false, false, 'User ID');
  }
}

const validateForm = (values) => {
  const errors = {};

  if (values['Notification Type'] && values['Notification Type'] !== values['Selected Notification Type']) {
    errors['Notification Type'] = 'Invalid Type';
  }

  if (values['Date From'] && !errors['Date From']) {
    errors['Date From'] = validate.date(values['Date From']);
  }

  if (values['Date To'] && !errors['Date To']) {
    errors['Date To'] = validate.date(values['Date To']);
  }

  if (values['Date From']
    && !validate.date(values['Date From'])
    && values['Date To']
    && !validate.date(values['Date To'])
    && moment(values['Date To'], dateFormat).isBefore(moment(values['Date From'], dateFormat))) {
    errors['Date To'] = 'Date To should be after Date From';
  }

  return errors;
};

const EventLogViewFilterForm = ({
  handleSubmit,
  dateRangeMin,
  dateRangeMax,
  account,
  change,
  dispatch,
  pristine,
  disabled,
  reset,
  classes,
}) => (
  <form
    className={classes.root}
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
  >
    <Grid
      container
      // wrap="nowrap"
      justify="flex-start"
      spacing={16}
    >
      <Grid item>
        <IconButton
          className={classes.buttonClear}
          onClick={() => {
            reset();
          }}
          disabled={pristine || disabled}
          tooltipContent="Clear all filters"
          tooltipPlacement="right"
        >
          <IconRefresh className={classes.iconRefresh} />
        </IconButton>
      </Grid>
      <Grid item xs={7} sm={4}>
        <Fields
          names={[
            'Date From',
            'Date To',
          ]}
          normalize={{
            'Date From': normalize.date,
            'Date To': normalize.date,
          }}
          startDateName="Date From"
          endDateName="Date To"
          component={DatepickerField}
          fullWidth
          minDate={dateRangeMin || '01/01/1900'}
          maxDate={dateRangeMax || moment().format(dateFormat)}
          dateFormat={dateFormat}
          placeholder="mm/dd/yyyy"
          maxLength="10"
          variant="range"
          size="sm"
          color="light"
          disabled={disabled}
          rangeButtons
        />
      </Grid>
      <Grid item xs={3} sm={2}>
        <Field
          id="Order ID"
          name="Order ID"
          placeholder="-Order ID-"
          component={TextField}
          normalize={normalize.positiveNumber}
          maxLength="6"
          size="sm"
          color="light"
          fullWidth
        />
      </Grid>   
      <Grid item xs={6} sm={3}>
        <Field
          id="User Name"
          name="User Name"
          component={AutosuggestField}
          placeholder="Assigned to User"
          fullWidth
          renderSuggestionContent={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
          renderInputContent={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
          size="sm"
          listWidth="auto"
          disabled={disabled}
          onSuggestionSelected={(suggestion) => {
            change('User ID', suggestion.ID);
            change('Selected User Name', `${suggestion['First Name']} ${suggestion['Last Name']}`);
            return `${suggestion['First Name']} ${suggestion['Last Name']}`;
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchUsers({
                filters: [
                  {
                    name: 'Role',
                    operation: 'ne',
                    val: 'System',
                  },
                  {
                    name: 'Full Name',
                    operation: 'startswith',
                    val: value,
                    case_sensitivity: false,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    dispatch(accountLoginFail());
                  }
                  return err;
                })
              : requestSearchUsers({
                filters: [
                  {
                    name: 'Role',
                    operation: 'ne',
                    val: 'System',
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    dispatch(accountLoginFail());
                  }
                  return err;
                })
          )}
        />
      </Grid>
      <Grid item xs={6} sm={2}>
        <Field
          id="Event"
          name="Event"
          component={AutosuggestField}
          placeholder="-Type-"
          fullWidth
          listWidth="auto"
          size="sm"
          color="light"
          disabled={disabled}
          renderSuggestionContent={suggestion => suggestion}
          onSuggestionSelected={suggestion => suggestion}
          fetchSuggestions={({ value }) => (new Promise((resolve) => {
            if (value.length > 0) {
              resolve(activityLogEventTypes
                .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
            } else {
              resolve(activityLogEventTypes);
            }
          }))}
          onlyConfirmedSelection
        />
      </Grid>
    </Grid>
  </form>
);

EventLogViewFilterForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  account: PropTypes.shape({
    ID: PropTypes.number,
  }),
  disabled: PropTypes.bool,
  dateRangeMin: PropTypes.string,
  dateRangeMax: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
  }).isRequired,
};

EventLogViewFilterForm.defaultProps = {
  dateRangeMin: null,
  dateRangeMax: null,
};

export default reduxForm({
  onChange,
  validate: validateForm,
})(withStyles(styles)(EventLogViewFilterForm));



// WEBPACK FOOTER //
// ./src/components/patient/EventLogViewDialog/EventLogViewFilterForm.js